import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {AnimalRepository} from "./states/animal.repository";
import {AnimalService} from "./services/animal.service";
import {debounceTime, skip, Subject, Subscription, take} from "rxjs";
import {AnimalModel} from "./models/animal.model";
import {AnimalAddService} from "./services/animal-add.service";
import {AnimalFilter} from "./animal-filters/animal-filter.model";
import {DataView, DataViewPaginatorState} from 'primeng/dataview';
import {AnimalWhereaboutsRepository} from "./states/animal-whereabouts.repository";
import {AnimalOriginRepository} from "./states/animal-origin.repository";
import { AnimalSheltersRepository } from '../../shared/states/animal-shelter.repository';
import { PaginatorState } from 'primeng/paginator';

@Component({
    selector: 'app-animal-overview',
    templateUrl: './animal-overview.component.html',
    styleUrls: ['./animal-overview.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class AnimalOverviewComponent implements OnInit, OnDestroy {
  @ViewChild('dv') dataView!: DataView;
  alive: boolean = true;
  animals: AnimalModel[] = [];
  filteredAnimals: AnimalModel[] = [];
  selectedFilterSubscription: Subscription;
  layout: "list" | "grid" = 'grid';
  first = 0;
  categoryFilters: AnimalFilter[] = [];
  searchTerm: string = '';
  searchTerm$ = new Subject<string>();
  rowCount: number = 3;
  addDialogVisible: boolean = false;
  whereabouts: any = new Map<number, any>();
  origins: any;
  duplicateAnimalDialogVisible: boolean = false;

  constructor(
    private readonly _animalRepository: AnimalRepository,
    private readonly _animalAddService: AnimalAddService,
    private readonly _animalWhereaboutsRepository: AnimalWhereaboutsRepository,
    private readonly _animalOriginRepository: AnimalOriginRepository,
    public animalService: AnimalService,
    private _animalSheltersRepository: AnimalSheltersRepository
  ) {
    this.selectedFilterSubscription = this.animalService.selectedFilters$
      .pipe(skip(1))
      .subscribe(() => {
        this.resetPage();
        this.filterAnimals();
        this.getVisibleAnimals();
        this.loadVisibleWhereabouts();
      });
  }

  ngOnInit(): void {


    this.layout = this.animalService.layout;
    this.rowCount = this.layout === 'grid' ? 3 : 10;

    this.searchTerm = this.animalService.searchTerm;

    if (this.searchTerm && this.searchTerm.length >= 2) {
      this.filterAnimals();
      this.searchTerm$.next(this.searchTerm);
    }

    this._animalAddService.addDialogVisible.subscribe((openModal) => {
      this.addDialogVisible = openModal;
    });

    this._animalRepository.getAnimals$().subscribe((animals: AnimalModel[]) => {
      this.animals = animals;
      this.categoryFilters = this.animalService.getFilterCategories();
      this.filterAnimals();
      this.first = (this.animalService.currentPage ?? 0) * this.rowCount;
      if (this.dataView) {
        this.dataView.first = this.first;
      }

      this.getVisibleAnimals();
      this.loadVisibleWhereabouts();
    });

    this.searchTerm$
    .pipe(debounceTime(1000))
    .subscribe(searchTerm => {
      const activeAnimalShelter = this._animalSheltersRepository.getActiveAnimalShelter()?.id;
      if (typeof activeAnimalShelter === 'number') {
        this._animalRepository.searchAnimalsAndUpdateStore(searchTerm, activeAnimalShelter);
      }
    });

    this._animalOriginRepository.getAll().pipe(take(1)).subscribe({
      next: (origins) => {
        if (origins && origins.length > 0) {
          this.origins = origins.reduce((acc: any, origin: any) => {
            acc.set(origin.animal?.id, origin);
            return acc;
          }, new Map<number, typeof origins[0]>());
        }
      }
    });

  }

  getVisibleAnimals(): number[] {
    if (!this.dataView) {
      return [];
    }

    const startIndex = this.dataView.first ?? 0;
    const endIndex = startIndex + this.rowCount;
    return this.filteredAnimals.slice(startIndex, endIndex).map(animal => animal.id);
  }

  loadVisibleWhereabouts(): void {
    const visibleAnimalIds = this.getVisibleAnimals();
    if (!visibleAnimalIds || visibleAnimalIds.length === 0) {
      return;
    }
    visibleAnimalIds.forEach((animalId) => {
      this._animalWhereaboutsRepository.getByAnimalId(animalId).pipe(take(1)).subscribe((whereabouts) => {
        if (Array.isArray(whereabouts) && whereabouts.length > 0) {
          const latestWhereabout = whereabouts.reduce((prev, current) => (prev.id > current.id ? prev : current));
          this.whereabouts.set(animalId, latestWhereabout);
        }
      });
    });
  }



  handleSearch(event: Event): void {
    if (!event.target) {
      return;
    }
    this.searchTerm = (event.target as HTMLInputElement).value;
    this.animalService.searchTerm = this.searchTerm;
    this.resetPage();

    this.filterAnimals();
    this.searchTerm$.next(this.searchTerm);
  }

  onSearchInput() {
    this.animalService.searchTerm = this.searchTerm;
    this.resetPage();

    this.filterAnimals();
    this.searchTerm$.next(this.searchTerm);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.animalService.searchTerm = this.searchTerm;
    this.resetPage();

    this.filterAnimals();
    this.searchTerm$.next(this.searchTerm);
  }

  filterAnimals(): void {
    const selectedFilters = this.animalService.getSelectedFilters();

    if (selectedFilters.length > 0) {
      this.filteredAnimals = this.animals.filter(animal =>
        selectedFilters.some(filter => animal.category?.toLowerCase() === filter.id.toLowerCase())
      );
    } else {
      this.filteredAnimals = [...this.animals];
    }

    if (this.searchTerm !== '') {
      const term = this.searchTerm.toLowerCase();

      this.filteredAnimals = this.filteredAnimals.filter(animal =>
        animal.displayName?.toLowerCase().includes(term) ||
        animal.nameNew?.toLowerCase().includes(term) ||
        animal.nameOld?.toLowerCase().includes(term) ||
        animal.name?.toLowerCase().includes(term) ||
        animal.bookNumber?.toLowerCase().includes(term) ||
        animal.transponderNumber?.includes(term) ||
        animal.petPassportNumber?.toLowerCase().includes(term) ||
        animal.oldBookNumber?.toLowerCase().includes(term)
      );
    }

    this.filteredAnimals.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });
    this.animalService.setSelectedFilters(selectedFilters);
    this.loadVisibleWhereabouts();
  }

  onLayoutChange(layout: any) {
    this.layout = layout;
    this.animalService.layout = layout;
    this.rowCount = layout === 'grid' ? 3 : 10;
    this.first = 0;
    if (this.dataView) {
      this.dataView.first = 0;
    }
    this.animalService.currentPage = 0;
    this.loadVisibleWhereabouts();
  }

  onPageChange(e: { first: number; rows: number }) {
    this.first = e.first ?? 0;
    const page = Math.floor(this.first / (e.rows ?? this.rowCount));
    this.animalService.currentPage = page;
    this.loadVisibleWhereabouts();
  }

  resetPage(): void {
    if (!this.dataView) {
      return;
    }
    this.animalService.currentPage = 0;
    this.first = 0;
    this.dataView.first = 0;
  }

  onListPageChange(e: { page: number; rows: number; first: number }) {
    this.animalService.currentPage = e.page;
    this.rowCount = e.rows;
    this.first = e.first;
  }

  ngOnDestroy(): void {
    this.alive = false;
    this.selectedFilterSubscription.unsubscribe();
  }

  toggleAddDialog(): void {
    this._animalAddService.toggleAddDialog(true);
  }
}
